import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { Column } from 'react-table'
import { Button, Dropdown, message, Modal, Select } from 'antd'
import type { MenuProps } from 'antd'
import { useBackgroundNavigate } from '@shared/hooks/use-background-navigate'
import * as Icons from '@ant-design/icons'

import { nextTableColumns } from '@shared/modules/next-table/helpers/next-table-columns'
import { StopPropagation } from '@shared/components/stop-propagation'
import { localURLMaker } from '@shared/utils/url-maker'
import { useSearchParams } from '@shared/hooks/use-search-params'

import { Product, ProductType } from '../types'
import { ProductsTableContext } from '../context'
import { ProductsRoutes } from '../constants/categories'
import { ProductsService } from '../services'
import { useProductCategoryList } from './use-category-list'
import { filterOption } from '@shared/modules/form/utils'
import { productCategoryQueryKeys } from '../utils'
import { OverCell } from '@shared/components/over-cell'
import { useStatusList } from '../../status/hooks/use-list'
import { getProductType } from '../helpers'
import { useAuth } from '@modules/me/hooks'

export const useProductsTable = () => {
  const { handleFetch } = useContext(ProductsTableContext)
  const navigate = useBackgroundNavigate()
  const searchParams = useSearchParams<{ reFetchProductsTable?: string }>()
  const { can } = useAuth()

  const categories = useProductCategoryList()
  const statusCollection = useStatusList({ modelId: 8 })

  const getRowProps = useCallback(
    (id) => ({
      onDoubleClick: () => navigate(localURLMaker('/products/:id/details', { id }), { withBackground: true }),
    }),
    [navigate],
  )

  const productTypeOptions = useMemo(
    () => [
      {
        id: ProductType.INVENTORY,
        name: getProductType(ProductType.INVENTORY),
      },
      {
        id: ProductType.PRODUCT,
        name: getProductType(ProductType.PRODUCT),
      },
    ],
    [],
  )

  const columns = useMemo<Column<Product>[]>(
    () => [
      {
        ...nextTableColumns.actions,
        Cell: ({ row: { original } }) => {
          const goTo = useCallback(
            (type: string) => {
              navigate(localURLMaker('/products/:id/:type', { id: original.id, type }), { withBackground: true })
            },
            [original.id],
          )

          const remove = useCallback(() => {
            const action = async () => {
              const result = await ProductsService.actionByIds([original.id], 'remove')

              if (result.status === 200) {
                message.success('Əməliyyat müvəffəqiyyətlə başa çatdı.')
                navigate(localURLMaker(ProductsRoutes.Index, {}, { reFetchProductsTable: true }))
              } else {
                message.error(result.data)
              }
            }

            Modal.confirm({
              title: 'Silməyə əminsinizmi?',
              content: 'Silmə əməliyyatını təsdiqlədikdən sonra məlumatların geri qaytarılması mümkün olmayacaq.',
              onOk: action,
            })
          }, [original.id])

          const menuItems: MenuProps['items'] = [
            can(41) && {
              key: 'update',
              label: 'Dəyişdir',
              icon: <Icons.EditOutlined />,
              onClick: () => goTo('update'),
            },
            { type: 'divider' },
            {
              key: 'details',
              label: 'Ətraflı',
              icon: <Icons.FileOutlined />,
              onClick: () => goTo('details'),
            },
            can(42) && {
              key: 'remove',
              label: 'Sil',
              icon: <Icons.DeleteOutlined />,
              onClick: remove,
            },
          ].filter(Boolean) as MenuProps['items']

          return (
            <StopPropagation>
              <Dropdown menu={{ items: menuItems }}>
                <Button size='small' icon={<Icons.MoreOutlined />} />
              </Dropdown>
            </StopPropagation>
          )
        },
      },
      {
        ...nextTableColumns.small,
        accessor: (row) => row.id,
        id: productCategoryQueryKeys.id,
        Header: 'Kod',
      },
      {
        accessor: (row) => row.name,
        id: productCategoryQueryKeys.productName,
        Header: 'Ad',
        Cell: OverCell,
      },
      {
        accessor: (row) => row.category.name,
        id: productCategoryQueryKeys.productCategory.id,
        Header: 'Kateqoriya',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select showSearch filterOption={filterOption} style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {categories.data?.map((category) => (
              <Select.Option key={category.id} value={category.id.toString()}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.normal,
        accessor: (row) => row.status.name,
        id: productCategoryQueryKeys.stateId,
        Header: 'Status',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select showSearch filterOption={filterOption} style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {statusCollection.data?.map((status) => (
              <Select.Option key={status.id} value={status.id.toString()}>
                {status.name}
              </Select.Option>
            ))}
          </Select>
        ),
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.barcode,
        id: productCategoryQueryKeys.barcode,
        Header: 'Barkod',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.itemCode,
        id: productCategoryQueryKeys.itemCode,
        Header: 'Məhsul kodu',
      },
      {
        ...nextTableColumns.large,
        accessor: (row) => row.type,
        id: productCategoryQueryKeys.type,
        Header: 'Tip',
        Filter: ({ column: { filterValue, setFilter } }) => (
          <Select style={{ width: '100%' }} value={filterValue} onChange={setFilter} allowClear={true}>
            {productTypeOptions.map((type) => (
              <Select.Option key={type.id} value={type.id.toString()}>
                {type.name}
              </Select.Option>
            ))}
          </Select>
        ),
        Cell: ({ cell: { value } }) => getProductType(value),
      },
    ],
    [can, navigate, categories.data, statusCollection.data, productTypeOptions],
  )

  useEffect(() => {
    (async () => {
      if (searchParams.searchParams.reFetchProductsTable) {
        searchParams.remove.current('reFetchProductsTable')
        await handleFetch()
      }
    })()
  }, [searchParams.searchParams.reFetchProductsTable, searchParams.remove, handleFetch])

  return { columns, getRowProps }
}
